"use client";

import { useState, useRef, useEffect } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { Canvas } from "@react-three/fiber";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ModelViewer from "../components/ModelViewer";
import LoginChecker from "../components/LoginCkecker";

import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import { useTranslation } from "react-i18next";
import {
  CloudUpload,
  Download,
  RefreshCcw as Refresh,
  Info,
  Settings,
  FileDown as FileDownload,
  CheckCircle as CheckCircleOutline,
  Box as ThreeDRotation,
  Ruler as Straighten,
  X,
  Loader2 as CircularProgress,
  Upload,
  Scale,
  Eye,
  Check,
  CircleDot,
  Home,
  Layers,
  Undo,
  Sparkles,
  ArrowLeft
} from "lucide-react";

// =============== WORKFLOW STEP COMPONENT ===============
const WorkflowStep = ({ step, index, isCompleted, isActive }) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex items-center group">
      <div className={`shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-300 ${
        isCompleted
          ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
          : isActive
            ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-4 ring-[#0f7699]/20 dark:ring-[#4fc3f7]/20"
            : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600"
      }`}>
        {isCompleted ? (
          <Check size={18} />
        ) : isActive ? (
          <CircleDot size={18} className="animate-pulse" />
        ) : (
          <span className="text-xs font-semibold">{index + 1}</span>
        )}
      </div>

      <div className="ml-4 lg:ml-5 whitespace-nowrap lg:whitespace-normal">
        <p className={`text-sm font-medium transition-colors duration-200 ${
          isActive
            ? "text-slate-900 dark:text-white"
            : isCompleted
              ? "text-[#0f7699] dark:text-[#4fc3f7]"
              : "text-slate-400 dark:text-slate-600"
        }`}>
          {step.label}
        </p>
        <span className={`text-xs ${
          isActive
            ? "text-[#0f7699] dark:text-[#4fc3f7] font-medium"
            : isCompleted
              ? "text-[#0f7699]/70 dark:text-[#4fc3f7]/70"
              : "text-slate-400 dark:text-slate-700"
        }`}>
          {isCompleted ? t("modelViewer.statusCompleted") : isActive ? t("modelViewer.statusInProgress") : t("modelViewer.statusPending")}
        </span>
      </div>
    </div>
  );
};

// =============== ICON BUTTON COMPONENT ===============
const IconButton = ({ icon: Icon, label, onClick, disabled, variant = "ghost" }) => {
  const baseStyle = "flex flex-col items-center justify-center space-y-1 md:space-y-2 transition-all duration-200 group";
  const iconStyle = `w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center border ${
    variant === "primary"
      ? "bg-[#0f7699] dark:bg-[#4fc3f7] border-[#0f7699] dark:border-[#4fc3f7] text-white hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90"
      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 group-hover:border-[#0f7699] dark:group-hover:border-[#4fc3f7] group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]"
  } ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer active:scale-95"}`;

  return (
    <button onClick={onClick} disabled={disabled} className={baseStyle}>
      <div className={iconStyle}>
        <Icon size={20} />
      </div>
      <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7] transition-colors">{label}</span>
    </button>
  );
};

// =============== CONTROL GROUP COMPONENT ===============
const ControlGroup = ({ title, children }) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg lg:rounded-xl p-2 md:p-4 flex-1 min-w-0 md:min-w-fit shadow-sm">
    <h4 className="hidden md:block text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-4">{title}</h4>
    <div className="flex items-center justify-evenly sm:justify-start sm:gap-2 md:gap-3 lg:space-x-6 flex-wrap">
      {children}
    </div>
  </div>
);

function PageModelViewer() {
  const { showNotification, confirm } = useNotification();
  const { t } = useTranslation();
  LoginChecker();

  const [objFile, setObjFile] = useState(null);
  const [textureFile, setTextureFile] = useState(null);
  const [scale, setScale] = useState(1);
  const [pointA, setPointA] = useState(null);
  const [pointB, setPointB] = useState(null);
  const [targetDistance, setTargetDistance] = useState(30);
  const [measuredDistance, setMeasuredDistance] = useState(null);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [scalingCompleted, setScalingCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [objContent, setObjContent] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 400 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("OBJ+PNG");
  const [showMesh, setShowMesh] = useState(false);
  const viewerRef = useRef(null);

  // Smooth tool state
  const [smoothIntensity, setSmoothIntensity] = useState(0);
  const [smoothApplied, setSmoothApplied] = useState(false);
  const [editingCompleted, setEditingCompleted] = useState(false);
  const [modifiedObjContent, setModifiedObjContent] = useState(null);
  const [originalObjFile, setOriginalObjFile] = useState(null);
  // Baseline content for live smoothing preview (before any smoothing)
  const [smoothBaseContent, setSmoothBaseContent] = useState(null);
  const [smoothBaseFile, setSmoothBaseFile] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 768);
  const [targetDistanceInput, setTargetDistanceInput] = useState("30.00");

  // Workflow steps
  const steps = [
    { id: "S1", label: t("modelViewer.stepUpload"), icon: Upload },
    { id: "S2", label: t("modelViewer.stepMeasure"), icon: Eye },
    { id: "S3", label: t("modelViewer.stepScale"), icon: Scale },
    { id: "S4", label: t("modelViewer.stepSmooth"), icon: Sparkles },
    { id: "S5", label: t("modelViewer.stepExport"), icon: Download }
  ];

  // Calculate current step
  const getCurrentStep = () => {
    if (!objFile) return 0;
    if (editingCompleted) return 4; // Export step
    if (scalingCompleted) return 3; // Smooth step
    if (!pointA || !pointB) return 1;
    if (!measuredDistance) return 2;
    return 3;
  };

  const currentStep = getCurrentStep();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (isMobile) {
        setCanvasSize({ width: 300, height: 250 });
      } else if (isTablet) {
        setCanvasSize({ width: 400, height: 350 });
      } else {
        setCanvasSize({ width: "100%", height: "100%" });
      }
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [isMobile, isTablet]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsLoading(true);

    try {
      const zipFile = files.find((file) => file.name.endsWith(".zip"));
      const obj = files.find((file) => file.name.endsWith(".obj"));
      const png = files.find((file) => file.name.endsWith(".png"));

      if (zipFile) {
        const zip = await JSZip.loadAsync(zipFile);
        const objFileInZip = Object.values(zip.files).find((f) =>
          f.name.endsWith(".obj")
        );
        const mtlFile = Object.values(zip.files).find((f) =>
          f.name.endsWith(".mtl")
        );

        if (objFileInZip) {
          const objBlob = await objFileInZip.async("blob");
          setObjFile(new File([objBlob], objFileInZip.name));

          // OBJ-Inhalt für den 3MF-Export speichern
          const objText = await objFileInZip.async("text");
          setObjContent(objText);
        }

        if (mtlFile) {
          const mtlText = await mtlFile.async("text");
          const match = mtlText.match(/map_Kd\s+(.*\.png)/);
          if (match && match[1]) {
            const texFile = Object.values(zip.files).find((f) =>
              f.name.endsWith(match[1])
            );
            if (texFile) {
              const blob = await texFile.async("blob");
              setTextureFile(new File([blob], texFile.name));
            }
          }
        }
      } else {
        if (obj) {
          setObjFile(obj);

          // OBJ-Inhalt für den 3MF-Export speichern
          const reader = new FileReader();
          reader.onload = (event) => {
            setObjContent(event.target.result);
          };
          reader.readAsText(obj);
        }
        if (png) setTextureFile(png);
      }

      setPointA(null);
      setPointB(null);
      setMeasuredDistance(null);
      setScale(1);
      setScalingCompleted(false);
    } catch (error) {
      console.error(t("errorMessages.fileProcessingError"), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTargetDistanceChange = (value) => {
    // Allow comma or period as decimal separator, max 2 decimal places
    const normalized = value.replace(",", ".");
    if (normalized === "" || /^\d*\.?\d{0,2}$/.test(normalized)) {
      setTargetDistanceInput(value);
      const num = parseFloat(normalized);
      if (!isNaN(num) && num >= 0) {
        setTargetDistance(num);
      }
    }
  };

  const handleStepBack = () => {
    if (editingCompleted) {
      setEditingCompleted(false);
      showNotification(t("modelViewer.backToSmoothing"), "info");
    } else if (scalingCompleted) {
      // Reset smooth state when going back to scaling
      handleResetSmooth();
      setSmoothBaseContent(null);
      setSmoothBaseFile(null);
      setScalingCompleted(false);
      showNotification(t("modelViewer.rescalePossible"), "info");
    }
  };

  const handleCalculateScale = () => {
    if (pointA && pointB) {
      const dist = pointA.distanceTo(pointB);
      const factor = targetDistance / dist;
      setScaleFactor(factor);
      setMeasuredDistance(dist);
      setScale((prev) => prev * factor);
      setScalingCompleted(true);
    }
  };

  const resetPoint = (p) => {
    if (viewerRef.current && viewerRef.current.resetMarker) {
      viewerRef.current.resetMarker(p);
    }
    if (p === "A") setPointA(null);
    if (p === "B") setPointB(null);
  };

  const handleDownload = (format) => {
    if (format === "OBJ+PNG") handleDownloadScaledModel();
    else if (format === "STL") handleDownloadSTL();
    else handleDownload3MF();
  };

  const handleDownloadScaledModel = async () => {
    if (!objFile) return;

    const zip = new JSZip();

    let finalContent;
    // Use modified content if available (already includes crop/smooth modifications)
    if (modifiedObjContent) {
      // Apply scaling to modified content
      finalContent = `mtllib scaled_model.mtl\n${modifiedObjContent.replace(/^mtllib .*/m, "")}`
        .split("\n")
        .map((line) =>
          line.startsWith("v ")
            ? line
                .split(/\s+/)
                .map((v, i) => (i > 0 ? parseFloat(v) * scale : v))
                .join(" ")
            : line
        )
        .join("\n");
    } else {
      // Use original file
      const text = await objFile.text();
      finalContent = `mtllib scaled_model.mtl\n${text.replace(/^mtllib .*/m, "")}`
        .split("\n")
        .map((line) =>
          line.startsWith("v ")
            ? line
                .split(" ")
                .map((v, i) => (i > 0 ? v * scale : v))
                .join(" ")
            : line
        )
        .join("\n");
    }

    zip.file("scaled_model.obj", finalContent);
    zip.file("scaled_model.mtl", `newmtl g0\nmap_Kd texture.png\n`);
    if (textureFile) zip.file("texture.png", textureFile);

    saveAs(
      await zip.generateAsync({ type: "blob" }),
      "scaled_model_with_texture.zip"
    );
  };

  const handleDownloadSTL = () => {
    const exporter = new STLExporter();
    if (viewerRef.current && viewerRef.current.getModelScene) {
      const data = exporter.parse(viewerRef.current.getModelScene());
      saveAs(new Blob([data], { type: "application/sla" }), "model.stl");
    }
  };

  // Konvertiert OBJ-Inhalt zu 3MF XML Format
  const objTo3MF = (content) => {
    const vertices = [];
    const texCoords = [];
    const triangles = [];

    content.split("\n").forEach((line) => {
      if (line.startsWith("v ")) {
        const parts = line.split(" ");
        const x = parseFloat(parts[1]) * scale;
        const y = parseFloat(parts[2]) * scale;
        const z = parseFloat(parts[3]) * scale;
        vertices.push({ x, y, z });
      } else if (line.startsWith("vt ")) {
        texCoords.push(line);
      } else if (line.startsWith("f ")) {
        triangles.push(line);
      }
    });

    const vertexXML = vertices.map(
      (v) => `\t\t\t\t\t<vertex x="${v.x}" y="${v.y}" z="${v.z}" />`
    );

    const texCoordXML = texCoords.map((line) => {
      const parts = line.split(" ");
      const u = parts[1];
      const v = parts[2];
      return `\t\t\t<m:tex2coord u="${u}" v="${v}" />`;
    });

    const triangleXML = triangles.map((line) => {
      const parts = line.split(" ").slice(1);

      const parseVertex = (vertex) => parseInt(vertex.split("/")[0], 10) - 1;
      const parseTexCoord = (vertex) => {
        const p = vertex.split("/");
        return p[1] ? parseInt(p[1], 10) - 1 : 0;
      };

      const vIndices = parts.map(parseVertex);
      const tIndices = parts.map(parseTexCoord);

      return `\t\t\t\t\t<triangle v1="${vIndices[0]}" v2="${vIndices[1]}" v3="${vIndices[2]}" pid="4" p1="${tIndices[0]}" p2="${tIndices[1]}" p3="${tIndices[2]}" />`;
    });

    return `<?xml version="1.0" encoding="utf-8"?>
<model xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" unit="millimeter" xml:lang="en-US" xmlns:m="http://schemas.microsoft.com/3dmanufacturing/material/2015/02">
\t<resources>
\t\t<m:texture2d id="1" path="/3D/Textures/baked_mesh_tex0.png" contenttype="image/png" tilestyleu="wrap" tilestylev="wrap" />
\t\t<m:texture2dgroup id="4" texid="1">
${texCoordXML.join("\n")}
\t\t</m:texture2dgroup>
\t\t<object id="5" type="model">
\t\t\t<mesh>
\t\t\t\t<vertices>
${vertexXML.join("\n")}
\t\t\t\t</vertices>
\t\t\t\t<triangles>
${triangleXML.join("\n")}
\t\t\t\t</triangles>
\t\t\t</mesh>
\t\t</object>
\t</resources>
\t<build>
\t\t<item objectid="5" />
\t</build>
</model>`;
  };

  const handleDownload3MF = async () => {
    if (!objContent) {
      showNotification(t("modelViewer.objNotLoaded"), "error");
      return;
    }

    try {
      const zip = new JSZip();
      const modelXml = objTo3MF(objContent);
      zip.file("3D/3dmodel.model", modelXml);

      if (textureFile) {
        const textureData = await textureFile.arrayBuffer();
        zip.file("3D/Textures/baked_mesh_tex0.png", textureData);
      }

      const contentTypes = `<?xml version="1.0" encoding="utf-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
\t<Default Extension="jpeg" ContentType="image/jpeg" />
\t<Default Extension="jpg" ContentType="image/jpeg" />
\t<Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml" />
\t<Default Extension="png" ContentType="image/png" />
\t<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
</Types>`;
      zip.file("[Content_Types].xml", contentTypes);

      const rels = `<?xml version="1.0" encoding="utf-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
\t<Relationship Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel" Target="/3D/3dmodel.model" Id="rel0" />
</Relationships>`;
      zip.file("_rels/.rels", rels);

      const modelRels = `<?xml version="1.0" encoding="utf-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
\t<Relationship Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dtexture" Target="/3D/Textures/baked_mesh_tex0.png" Id="rel1" />
</Relationships>`;
      zip.file("3D/_rels/3dmodel.model.rels", modelRels);

      const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
      const blob = new Blob([content], { type: "model/3mf" });
      saveAs(blob, "model.3mf");

      showNotification(t("modelViewer.threeMfExportSuccess"), "success");
    } catch (error) {
      console.error("3MF Export Fehler:", error);
      showNotification(t("modelViewer.threeMfExportError"), "error");
    }
  };

  const doResetAll = () => {
    setObjFile(null);
    setTextureFile(null);
    setObjContent(null);
    setPointA(null);
    setPointB(null);
    setMeasuredDistance(null);
    setScale(1);
    setScaleFactor(1);
    setScalingCompleted(false);
    setShowMesh(false);
    // Reset smooth state
    setSmoothIntensity(0);
    setSmoothApplied(false);
    setEditingCompleted(false);
    setModifiedObjContent(null);
    setOriginalObjFile(null);
    setSmoothBaseContent(null);
    setSmoothBaseFile(null);
  };

  const resetAll = async () => {
    const confirmed = await confirm(
      t("modelViewer.resetConfirmMessage"),
      {
        title: t("modelViewer.resetTitle"),
        confirmText: t("modelViewer.resetTitle"),
        cancelText: t("modelViewer.cancelButton")
      }
    );
    if (confirmed) {
      doResetAll();
    }
  };

  // Store baseline content when entering smooth step
  useEffect(() => {
    if (scalingCompleted && !editingCompleted && !smoothBaseContent) {
      setSmoothBaseContent(objContent);
      setSmoothBaseFile(objFile);
      if (!originalObjFile) {
        setOriginalObjFile(objFile);
      }
    }
  }, [scalingCompleted, editingCompleted, smoothBaseContent, objContent, objFile, originalObjFile]);

  // Live smoothing preview — apply via viewerRef in-place (no model reload = camera + mesh preserved)
  const handleSmoothIntensityChange = async (newIntensity) => {
    const intensity = Math.max(0, Math.min(20, newIntensity));
    setSmoothIntensity(intensity);

    if (!viewerRef.current || !smoothBaseContent) return;

    try {
      // applySmooth updates the 3D geometry in-place via the viewer ref
      const newObjContent = viewerRef.current.applySmooth(intensity, smoothBaseContent, scale);
      if (newObjContent) {
        setModifiedObjContent(intensity > 0 ? newObjContent : null);
        setSmoothApplied(intensity > 0);
      }
    } catch (error) {
      console.error("Smooth preview error:", error);
    }
  };

  // Confirm smoothing and move to export step
  const handleConfirmSmooth = () => {
    // Persist the smoothed model for export/download
    if (smoothApplied && modifiedObjContent) {
      setObjContent(modifiedObjContent);
      const blob = new Blob([modifiedObjContent], { type: 'text/plain' });
      setObjFile(new File([blob], 'smoothed_model.obj'));
      showNotification(t("modelViewer.smoothingConfirmed"), "success");
    } else {
      showNotification(t("modelViewer.continueWithoutSmoothing"), "info");
    }
    setEditingCompleted(true);
  };

  // Reset smoothing to baseline (in-place via viewer)
  const handleResetSmooth = () => {
    if (viewerRef.current && smoothBaseContent) {
      // Apply intensity 0 = restore original geometry in-place
      viewerRef.current.applySmooth(0, smoothBaseContent, scale);
    }
    setModifiedObjContent(null);
    setSmoothApplied(false);
    setSmoothIntensity(0);
    showNotification(t("modelViewer.smoothingReset"), "info");
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans select-none flex flex-col lg:overflow-hidden">
      {/* Page Header - Full Width */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <ThreeDRotation className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("modelViewer.pageTitle")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("modelViewer.pageSubtitle")}
              </p>
            </div>
            {/* Model Info in Header */}
            {objFile && (
              <div className="hidden md:flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="w-9 h-9 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-lg flex items-center justify-center shrink-0">
                  <ThreeDRotation className="text-[#0f7699] dark:text-[#4fc3f7]" size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate max-w-[200px]">{objFile.name}</p>
                  {textureFile && (
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[200px]">+ {textureFile.name}</p>
                  )}
                </div>
                <button
                  onClick={() => document.getElementById('fileInput').click()}
                  className="ml-2 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                >
                  {t("modelViewer.changeButton")}
                </button>
                <input id="fileInput" type="file" hidden multiple onChange={handleFileChange} accept=".zip,.obj,.png" />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="flex flex-col lg:flex-row flex-1 lg:min-h-0 lg:overflow-hidden">
        {/* WORKFLOW STEPPER - Left Sidebar */}
        <nav className="w-full lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800 px-3 py-2 lg:p-6 lg:pt-8 bg-white dark:bg-slate-900/50 z-30 flex flex-col lg:overflow-y-auto">
          <h3 className="hidden lg:block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-8">{t("modelViewer.workflowSteps")}</h3>

          {/* Horizontal stepper for mobile */}
          <div className="flex lg:hidden items-center justify-center gap-0 pb-1">
            {steps.map((step, idx) => {
              const isCompleted = currentStep > idx;
              const isActive = currentStep === idx;

              return (
                <div key={step.id} className="flex items-center">
                  <div className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                    isCompleted
                      ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
                      : isActive
                        ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-2 ring-[#0f7699]/20 dark:ring-[#4fc3f7]/20"
                        : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400"
                  }`}>
                    {isCompleted ? <Check size={12} /> : <span className="text-[10px] font-semibold">{idx + 1}</span>}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-5 h-0.5 ${isCompleted ? 'bg-[#0f7699]' : 'bg-slate-200 dark:bg-slate-700'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress info for mobile */}
          <div className="lg:hidden mt-1 text-center">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
              {steps[Math.max(0, Math.min(currentStep, steps.length - 1))].label}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-2">
              {currentStep > steps.length - 1 ? t("modelViewer.statusCompleted") : currentStep === 0 && !objFile ? t("modelViewer.statusPending") : t("modelViewer.statusInProgress")}
            </span>
          </div>

          {/* Vertical stepper for desktop */}
          <div className="hidden lg:block relative space-y-8">
            <div className="absolute left-[19px] top-5 bottom-5 w-0.5 bg-slate-200 dark:bg-slate-800" />

            {steps.map((step, idx) => {
              const isCompleted = currentStep > idx;
              const isActive = currentStep === idx;

              return (
                <WorkflowStep
                  key={step.id}
                  step={step}
                  index={idx}
                  isCompleted={isCompleted}
                  isActive={isActive}
                />
              );
            })}
          </div>

        </nav>

        {/* MAIN CONTENT AREA */}
        <main className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950 relative lg:min-h-0 lg:overflow-hidden">
          {/* Main content section */}
          <section className={`flex-1 flex flex-col items-center p-3 md:p-8 lg:overflow-y-auto ${!objFile ? 'justify-center' : ''}`}>
            {/* No file uploaded state */}
            {!objFile ? (
              <div
                className="max-w-lg w-full p-8 md:p-12 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-900 text-center hover:border-[#0f7699] dark:hover:border-[#4fc3f7] transition-colors cursor-pointer group"
                onClick={() => document.getElementById('fileInput').click()}
              >
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#0f7699]/5 dark:group-hover:bg-[#4fc3f7]/15 transition-colors">
                  <CloudUpload className="text-slate-400 dark:text-slate-500 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#0f7699] dark:text-[#4fc3f7]">{t("modelViewer.uploadTitle")}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{t("modelViewer.uploadDescription")}</p>
                <button className="px-8 py-3 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors shadow-sm">
                  {t("modelViewer.selectFile")}
                </button>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">{t("modelViewer.supportedFormats")}</p>
                <input id="fileInput" type="file" hidden multiple onChange={handleFileChange} accept=".zip,.obj,.png" />
              </div>
            ) : (
              /* 3D Viewer */
              <div className="w-full flex-1 max-w-5xl flex flex-col min-h-0 px-4 md:px-0">
                {/* 3D Canvas Container */}
                <div className="flex-1 min-h-[200px] sm:min-h-[400px] bg-slate-900 dark:bg-slate-950 rounded-2xl border border-slate-800 dark:border-slate-700 shadow-inner relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="h-full w-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:30px_30px]" />
                  </div>
                  <div className="absolute inset-0">
                    <Canvas className="cursor-move w-full h-full">
                      <ModelViewer
                        ref={viewerRef}
                        scale={scale}
                        setPointA={setPointA}
                        setPointB={setPointB}
                        objFile={objFile}
                        textureFile={textureFile}
                        pointA={pointA}
                        pointB={pointB}
                      />
                    </Canvas>
                  </div>

                  {/* Viewer HUD - hidden on mobile */}
                  <div className="absolute top-4 left-4 hidden sm:flex items-center gap-2">
                    <div className="px-3 py-1.5 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-lg flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#4fc3f7] animate-pulse" />
                      <span className="text-[8px] font-black text-white uppercase tracking-widest">Live</span>
                    </div>
                  </div>

                  {/* Viewer Controls - Home & Texture Only (top right) - Desktop only */}
                  <div className="absolute top-4 right-4 hidden sm:flex gap-2">
                    <button
                      onClick={() => {
                        if (viewerRef.current?.resetCamera) {
                          viewerRef.current.resetCamera();
                        }
                      }}
                      className="px-4 py-2.5 bg-slate-800/90 backdrop-blur-xl border border-slate-600/50 rounded-xl text-white hover:bg-slate-700/90 transition-all active:scale-95 flex items-center gap-2"
                    >
                      <Home size={18} />
                      <span className="text-sm font-medium">Home</span>
                    </button>
                    <button
                      onClick={() => {
                        if (viewerRef.current?.toggleMeshMode) {
                          viewerRef.current.toggleMeshMode();
                          setShowMesh(prev => !prev);
                        }
                      }}
                      className={`px-4 py-2.5 backdrop-blur-xl border rounded-xl transition-all active:scale-95 flex items-center gap-2 ${
                        showMesh
                          ? 'bg-[#4fc3f7] border-[#4fc3f7] text-slate-900 hover:bg-[#4fc3f7]/90'
                          : 'bg-slate-800/90 border-slate-600/50 text-white hover:bg-slate-700/90'
                      }`}
                    >
                      <Layers size={18} />
                      <span className="text-sm font-medium">{showMesh ? 'Textur' : 'Mesh'}</span>
                    </button>
                  </div>

                  {/* Scale info (below buttons) - Desktop only */}
                  <div className="absolute top-20 right-4 hidden sm:block px-3 py-1.5 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-lg">
                    <span className="text-[10px] font-bold text-white">{t("modelViewer.scalingLabel")}: {scale.toFixed(2)}×</span>
                  </div>

                  {/* Smooth indicator - desktop only */}
                  {scalingCompleted && !editingCompleted && smoothIntensity > 0 && (
                    <div className="absolute top-32 right-4 hidden sm:block px-3 py-2 bg-purple-500/90 backdrop-blur-xl border border-white/20 rounded-lg">
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">
                        {t("modelViewer.smoothingLabel")}: {smoothIntensity}
                      </span>
                    </div>
                  )}

                  {/* Points status (bottom right) - hidden on mobile */}
                  <div className="absolute bottom-4 right-4 hidden sm:flex gap-2">
                    <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${pointA ? 'bg-red-500/80' : 'bg-slate-900/80'} backdrop-blur-xl border border-white/10`}>
                      <span className={`text-[10px] font-black uppercase tracking-widest ${pointA ? 'text-white' : 'text-red-400'}`}>{t("modelViewer.pointA")}</span>
                      {pointA && <Check size={12} className="text-white" />}
                    </div>
                    <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${pointB ? 'bg-blue-500/80' : 'bg-slate-900/80'} backdrop-blur-xl border border-white/10`}>
                      <span className={`text-[10px] font-black uppercase tracking-widest ${pointB ? 'text-white' : 'text-blue-400'}`}>{t("modelViewer.pointB")}</span>
                      {pointB && <Check size={12} className="text-white" />}
                    </div>
                  </div>

                  {/* Control hints (bottom left) - hidden on touch devices */}
                  <div className="hidden sm:block absolute bottom-4 left-4 bg-slate-900/70 backdrop-blur-sm rounded-lg px-3 py-2.5 text-white text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 border border-white/40 rounded text-[8px] flex items-center justify-center">🖱</span>
                      <span className="text-white/80">Left Drag: Rotate</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 border border-white/40 rounded text-[8px] flex items-center justify-center">🖱</span>
                      <span className="text-white/80">Right Drag: Pan</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">⚙</span>
                      <span className="text-white/80">Scroll: Zoom</span>
                    </div>
                  </div>
                </div>


              </div>
            )}
          </section>

          {/* Mobile: Viewer controls bar between 3D view and footer */}
          {objFile && (
            <div className="sm:hidden shrink-0 flex items-center justify-between px-2 py-1.5 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => { if (viewerRef.current?.resetCamera) viewerRef.current.resetCamera(); }}
                  className="h-8 px-2.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 active:scale-95 transition-all flex items-center gap-1.5"
                >
                  <Home size={14} />
                  <span className="text-[10px] font-bold">Home</span>
                </button>
                <button
                  onClick={() => { if (viewerRef.current?.toggleMeshMode) { viewerRef.current.toggleMeshMode(); setShowMesh(prev => !prev); } }}
                  className={`h-8 px-2.5 border rounded-lg active:scale-95 transition-all flex items-center gap-1.5 ${
                    showMesh
                      ? 'bg-[#0f7699] dark:bg-[#4fc3f7] border-[#0f7699] dark:border-[#4fc3f7] text-white dark:text-slate-900'
                      : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                  }`}
                >
                  <Layers size={14} />
                  <span className="text-[10px] font-bold">{showMesh ? 'Textur' : 'Mesh'}</span>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                  <span className="text-[9px] font-medium text-slate-500 dark:text-slate-400">{t("modelViewer.scaleShort")}</span>
                  <span className="text-[10px] font-black text-[#0f7699] dark:text-[#4fc3f7]">{scale.toFixed(2)}×</span>
                </div>
                {scalingCompleted && !editingCompleted && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                    <span className="text-[9px] font-medium text-slate-500 dark:text-slate-400">{t("modelViewer.smoothingLabel")}</span>
                    <span className="text-[10px] font-black text-purple-600 dark:text-purple-400">{smoothIntensity}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer with controls */}
          <footer className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-row flex-wrap items-stretch p-2 md:p-3 md:px-6 gap-1.5 lg:gap-4 z-50">
            {/* Skalierung Control Group - hide on mobile after scaling */}
            <div className={scalingCompleted ? 'hidden sm:contents' : 'contents'}>
            <ControlGroup title={t("modelViewer.scalingTitle")}>
              {/* Reset A - Red colored (destructive = left) */}
              <button
                onClick={() => resetPoint("A")}
                disabled={!pointA}
                className="flex flex-col items-center justify-center space-y-2 transition-all duration-200 group"
              >
                <div className={`w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center border ${
                  !pointA
                    ? "opacity-30 cursor-not-allowed bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                    : "cursor-pointer active:scale-95 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30"
                }`}>
                  <Refresh size={20} />
                </div>
                <span className={`text-[10px] font-medium transition-colors ${pointA ? 'text-red-500 dark:text-red-400' : 'text-slate-400'}`}>Reset A</span>
              </button>
              {/* Reset B - Blue colored (destructive = left) */}
              <button
                onClick={() => resetPoint("B")}
                disabled={!pointB}
                className="flex flex-col items-center justify-center space-y-2 transition-all duration-200 group"
              >
                <div className={`w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center border ${
                  !pointB
                    ? "opacity-30 cursor-not-allowed bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                    : "cursor-pointer active:scale-95 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-500 dark:text-blue-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30"
                }`}>
                  <Refresh size={20} />
                </div>
                <span className={`text-[10px] font-medium transition-colors ${pointB ? 'text-blue-500 dark:text-blue-400' : 'text-slate-400'}`}>Reset B</span>
              </button>
              {/* Distanz · Faktor Anzeige (info = center) */}
              <div className="flex flex-col items-center space-y-1 md:space-y-2">
                <div className="h-10 md:h-11 flex items-center px-1.5 md:px-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700 gap-1">
                  <span className="text-[10px] font-black text-[#0f7699] dark:text-[#4fc3f7]">{measuredDistance ? measuredDistance.toFixed(2) : "0.00"}</span>
                  <span className="text-slate-300 dark:text-slate-600 text-[8px]">|</span>
                  <span className="text-[10px] font-black text-slate-900 dark:text-white">{scaleFactor ? scaleFactor.toFixed(2) : "1.00"}×</span>
                </div>
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{t("modelViewer.distanceFactor")}</span>
              </div>
              {/* Ziel-Distanz Eingabe (setting = center-right) */}
              <div className="flex flex-col items-center space-y-2 group">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={targetDistanceInput}
                    onChange={(e) => handleTargetDistanceChange(e.target.value)}
                    className="w-16 h-11 px-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:border-[#0f7699] dark:focus:border-[#4fc3f7] outline-none transition-all font-bold text-slate-700 dark:text-slate-200 text-sm text-center"
                  />
                </div>
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{t("modelViewer.targetMm")}</span>
              </div>
              {/* Skalieren Button (primary = right) */}
              <IconButton
                icon={Scale}
                label={t("modelViewer.scaleButton")}
                variant="primary"
                disabled={!pointA || !pointB}
                onClick={handleCalculateScale}
              />
            </ControlGroup>
            </div>

            {/* Glättung Control Group - Desktop only */}
            {scalingCompleted && !editingCompleted && (
              <div className="hidden sm:contents">
                <ControlGroup title={t("modelViewer.smoothingLabel")}>
                  <div className="flex flex-col space-y-2 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400">{t("modelViewer.smoothingFactor")}</span>
                      <input type="range" min={0} max={20} value={smoothIntensity} onChange={(e) => handleSmoothIntensityChange(Number(e.target.value))} className="flex-1 h-1.5 accent-[#0f7699]" />
                      <span className="text-[10px] font-bold w-5 text-slate-700 dark:text-slate-200 text-center">{smoothIntensity}</span>
                    </div>
                  </div>
                  <IconButton icon={Sparkles} label={t("modelViewer.confirmButton")} variant="primary" onClick={handleConfirmSmooth} />
                  {smoothApplied && (
                    <IconButton icon={Undo} label={t("modelViewer.resetTitle")} onClick={handleResetSmooth} />
                  )}
                </ControlGroup>
              </div>
            )}

            {/* Mobile: Step 4 — UX order: destructive left, settings center, primary right */}
            {scalingCompleted && !editingCompleted && (
              <div className="sm:hidden flex-1">
                <ControlGroup title={t("modelViewer.smoothingLabel")}>
                  <IconButton icon={Refresh} label={t("modelViewer.resetButton")} onClick={resetAll} />
                  <IconButton icon={ArrowLeft} label={t("modelViewer.backButton")} onClick={handleStepBack} />
                  {/* Touch-friendly +/- stepper */}
                  <div className="flex flex-col items-center space-y-1">
                    <div className="flex items-center h-10 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <button
                        onClick={() => handleSmoothIntensityChange(smoothIntensity - 1)}
                        disabled={smoothIntensity <= 0}
                        className="w-9 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 active:bg-slate-200 dark:active:bg-slate-700 disabled:opacity-30 transition-colors font-bold text-lg"
                      >
                        −
                      </button>
                      <div className="w-8 h-full flex items-center justify-center bg-white dark:bg-slate-900 text-[12px] font-black text-[#0f7699] dark:text-[#4fc3f7]">
                        {smoothIntensity}
                      </div>
                      <button
                        onClick={() => handleSmoothIntensityChange(smoothIntensity + 1)}
                        disabled={smoothIntensity >= 20}
                        className="w-9 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 active:bg-slate-200 dark:active:bg-slate-700 disabled:opacity-30 transition-colors font-bold text-lg"
                      >
                        +
                      </button>
                    </div>
                    <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{t("modelViewer.smoothingFactor")}</span>
                  </div>
                  <IconButton icon={Download} label={t("modelViewer.exportLabel")} disabled={!objFile} onClick={() => setIsModalOpen(true)} />
                  <IconButton icon={Sparkles} label={t("modelViewer.confirmButton")} variant="primary" onClick={handleConfirmSmooth} />
                </ControlGroup>
              </div>
            )}

            {/* Mobile: Step 5 (Export) — UX order: destructive left, back center, primary right */}
            {scalingCompleted && editingCompleted && (
              <div className="sm:hidden flex-1">
                <ControlGroup title={t("modelViewer.exportLabel")}>
                  <IconButton icon={Refresh} label={t("modelViewer.resetButton")} onClick={resetAll} />
                  <IconButton icon={ArrowLeft} label={t("modelViewer.backButton")} onClick={handleStepBack} />
                  <IconButton icon={Download} label={t("modelViewer.exportLabel")} variant="primary" disabled={!objFile} onClick={() => setIsModalOpen(true)} />
                </ControlGroup>
              </div>
            )}

            {/* Desktop: Export + Aktionen - always visible */}
            <div className="hidden sm:contents">
              <ControlGroup title={t("modelViewer.exportLabel")}>
                <IconButton
                  icon={Download}
                  label="Download"
                  disabled={!objFile}
                  onClick={() => setIsModalOpen(true)}
                />
                <IconButton
                  icon={FileDownload}
                  label="OBJ+PNG"
                  disabled={!objFile}
                  onClick={handleDownloadScaledModel}
                />
                <IconButton
                  icon={FileDownload}
                  label="STL"
                  disabled={!objFile}
                  onClick={handleDownloadSTL}
                />
                <IconButton
                  icon={FileDownload}
                  label="3MF"
                  disabled={!objFile}
                  onClick={handleDownload3MF}
                />
              </ControlGroup>

              <ControlGroup title={t("modelViewer.actionsTitle")}>
                <IconButton
                  icon={CloudUpload}
                  label={t("modelViewer.loadModel")}
                  variant="primary"
                  onClick={() => document.getElementById('fileInput').click()}
                />
                <IconButton
                  icon={Refresh}
                  label={t("modelViewer.resetTitle")}
                  onClick={resetAll}
                />
              </ControlGroup>
            </div>
          </footer>
        </main>
      </div>

      {/* Modal: Choose Download Format */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-white dark:border-slate-800 animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0f7699] dark:bg-[#4fc3f7] rounded-2xl flex items-center justify-center text-white dark:text-slate-900 shadow-lg">
                  <Download size={24} />
                </div>
                <h2 className="text-xl font-bold text-[#0f7699] dark:text-[#4fc3f7]">{t("modelViewer.chooseDownloadFormat")}</h2>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 sm:p-8 md:p-10 space-y-6 sm:space-y-8">
              <div className="space-y-3 sm:space-y-4">
                {["OBJ+PNG", "STL", "3MF"].map(format => (
                  <div
                    key={format}
                    onClick={() => setDownloadFormat(format)}
                    className={`p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-2 transition-all cursor-pointer flex items-center gap-4 sm:gap-6 group ${
                      downloadFormat === format
                        ? "border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10"
                        : "border-slate-100 dark:border-slate-700 hover:border-[#0f7699]/50 dark:hover:border-[#4fc3f7]/50 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${
                      downloadFormat === format
                        ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 group-hover:bg-[#0f7699]/10 dark:group-hover:bg-[#4fc3f7]/10 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]"
                    }`}>
                      <FileDownload size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-bold transition-colors ${downloadFormat === format ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-slate-800 dark:text-slate-200"}`}>{format}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {format === "OBJ+PNG" && t("modelViewer.formatObjDesc")}
                        {format === "STL" && t("modelViewer.formatStlDesc")}
                        {format === "3MF" && t("modelViewer.formatThreeMfDesc")}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-4 transition-all ${
                      downloadFormat === format ? "border-[#0f7699] dark:border-[#4fc3f7] bg-white dark:bg-slate-900" : "border-slate-200 dark:border-slate-600"
                    }`} />
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 h-14 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  {t("modelViewer.cancelButton")}
                </button>
                <button
                  onClick={() => { handleDownload(downloadFormat); setIsModalOpen(false); }}
                  className="flex-1 h-14 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-2xl font-bold uppercase tracking-widest text-[11px] shadow-xl shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20 hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-all"
                >
                  {t("modelViewer.exportModelButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Backdrop */}
      {isLoading && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-[#0f7699]/40 dark:bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-500">
          <div className="text-center space-y-8 p-12 bg-white/10 dark:bg-slate-800/50 border border-white/20 dark:border-slate-700 rounded-[3rem] shadow-2xl">
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 border-8 border-white/10 dark:border-[#4fc3f7]/20 rounded-full" />
              <CircularProgress size={96} className="text-white dark:text-[#4fc3f7] animate-spin stroke-[4]" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-white uppercase tracking-tight">{t("modelViewer.processingFiles")}</h3>
              <p className="text-[#4fc3f7]/80 dark:text-slate-400 text-sm font-medium">{t("modelViewer.pleaseWait")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PageModelViewer;
